import {Input} from "../../../basic/input";
import {Label} from "../../../basic/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "../../../basic/select";
import {Checkbox} from "../../../basic/checkbox";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {VwpUser} from "../../../../store/generated/api.ts";

type UserFlag = NonNullable<VwpUser["flags"]>[number];

interface UserFormProps {
    selectedUser: VwpUser | null;
    editedUserData: EditedUserData;
    setEditedUserData: (data: EditedUserData) => void;
}

export interface EditedUserData {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: VwpUser["role"];
    status?: VwpUser["status"];
    internalId?: string;
    flags?: UserFlag[];
}

const USER_FLAGS: UserFlag[] = ["BYPASS_HARDWARE_LOCKS", "HAS_ACCEPTED_TOS", "WAS_STUDENT", "ALUMNI_ELIGIBLE", "IS_ALUMNI"];

export function UserForm({selectedUser, editedUserData, setEditedUserData}: UserFormProps) {
    const {t} = useTranslationWithKeys();
    const selectedFlags = editedUserData.flags ?? selectedUser?.flags ?? [];

    const toggleFlag = (flag: UserFlag, checked: boolean) => {
        const nextFlags = checked
            ? (selectedFlags.includes(flag) ? selectedFlags : [...selectedFlags, flag])
            : selectedFlags.filter((currentFlag) => currentFlag !== flag);

        setEditedUserData({...editedUserData, flags: nextFlags});
    };

    return (
        <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <Label htmlFor="firstName">{t('admin.users.editUser.firstName')}</Label>
                <Input
                    id="firstName"
                    value={editedUserData.firstName || selectedUser?.firstName || ""}
                    onChange={(e) => setEditedUserData({...editedUserData, firstName: e.target.value})}
                    placeholder={t('admin.users.editUser.firstNamePlaceholder')}
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="lastName">{t('admin.users.editUser.lastName')}</Label>
                <Input
                    id="lastName"
                    value={editedUserData.lastName || selectedUser?.lastName || ""}
                    onChange={(e) => setEditedUserData({...editedUserData, lastName: e.target.value})}
                    placeholder={t('admin.users.editUser.lastNamePlaceholder')}
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="email">{t('admin.users.editUser.emailAddress')}</Label>
                <Input
                    id="email"
                    type="email"
                    value={editedUserData.email || selectedUser?.email || ""}
                    onChange={(e) => setEditedUserData({...editedUserData, email: e.target.value})}
                    placeholder={t('admin.users.editUser.emailPlaceholder')}
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="internalId">Internal ID</Label>
                <Input
                    id="internalId"
                    value={editedUserData.internalId ?? selectedUser?.internalId ?? ""}
                    onChange={(e) => setEditedUserData({...editedUserData, internalId: e.target.value})}
                    placeholder="Enter internal ID"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="role">{t('admin.users.editUser.role')}</Label>
                <Select
                    value={editedUserData.role || selectedUser?.role || "USER"}
                    onValueChange={(value) => setEditedUserData({...editedUserData, role: value as VwpUser["role"]})}
                >
                    <SelectTrigger id="role">
                        <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ADMIN">{t('common.role.admin')}</SelectItem>
                        <SelectItem value="USER">{t('common.role.user')}</SelectItem>
                        {(selectedUser?.team?.isUniversity || selectedUser?.role === "STUDENT") && (
                            <SelectItem value="STUDENT">{t('common.role.student')}</SelectItem>
                        )}
                    </SelectContent>
                </Select>
                <p className="text-muted-foreground">
                    {t('admin.users.editUser.roleDescription')}
                </p>
            </div>

            <div className="space-y-2">
                <Label htmlFor="status">{t('admin.users.editUser.accountStatus')}</Label>
                <Select
                    value={editedUserData.status || selectedUser?.status || "ACTIVE"}
                    onValueChange={(value) => setEditedUserData({...editedUserData, status: value as VwpUser["status"]})}
                >
                    <SelectTrigger id="status">
                        <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ACTIVE">{t('admin.users.status.active')}</SelectItem>
                        <SelectItem value="DISABLED">{t('admin.users.status.inactive')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
                <Label>Flags</Label>
                <div className="grid grid-cols-2 gap-3">
                    {USER_FLAGS.map((flag) => {
                        const checked = selectedFlags.includes(flag);
                        return (
                            <label key={flag} className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    checked={checked}
                                    onCheckedChange={(value) => toggleFlag(flag, !!value)}
                                />
                                <Label>{flag}</Label>
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}